import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck, faCalendarAlt, faSave, faFileExcel, faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import { useAuth } from '../../contexts/AuthContext';

const InterviewerAttendance = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === 'supervisor_cati';
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [availableYears, setAvailableYears] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [perDaySurveys, setPerDaySurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    fetchAttendanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedDay]);

  const fetchAvailableYears = async () => {
    try {
      const response = await axios.get('/cati/attendance/years');
      const years = response.data.years || [];
      const merged = years.includes(today.getFullYear()) ? years : [today.getFullYear(), ...years];
      setAvailableYears(merged.sort((a, b) => b - a));
    } catch (error) {
      console.error('Error fetching available years:', error);
      setAvailableYears([today.getFullYear()]);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const response = await axios.get('/cati/attendance', { params: { date: dateStr } });

      const data = response.data.data || [];
      const surveys = response.data.per_day_surveys || [];

      // For unconfirmed rows where the user hasn't picked surveys yet,
      // pre-select the surveys their completes belong to. This is a one-time
      // hint — once the user touches it, we don't overwrite.
      const processed = data.map(item => {
        const hasUserSelection = Array.isArray(item.survey_ids) && item.survey_ids.length > 0;
        const survey_ids = (!item.confirmed && !hasUserSelection)
          ? (item.survey_ids_from_completes || [])
          : (item.survey_ids || []);
        return {
          ...item,
          day_type: item.day_type || null,
          survey_ids,
        };
      });

      setAttendanceData(processed);
      setPerDaySurveys(surveys);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData([]);
      setPerDaySurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getAvailableDays = () => Array.from({ length: getDaysInMonth(selectedMonth, selectedYear) }, (_, i) => i + 1);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    const dim = getDaysInMonth(selectedMonth, year);
    if (selectedDay > dim) setSelectedDay(dim);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    const dim = getDaysInMonth(month, selectedYear);
    if (selectedDay > dim) setSelectedDay(dim);
  };

  const isToday = () => {
    const sel = new Date(selectedYear, selectedMonth - 1, selectedDay);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return sel.getTime() === t.getTime();
  };

  const updateRow = (interviewerId, patch) => {
    setAttendanceData(prev => prev.map(item =>
      item.interviewer_id === interviewerId ? { ...item, ...patch } : item
    ));
  };

  const handleSave = async () => {
    if (!isToday()) return;
    setSaving(true);
    setSaveMessage('');
    setSaveError('');
    try {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const payload = {
        date: dateStr,
        // Send rows with a day_type (to upsert) AND rows that were registered but are now cleared
        // (day_type null + confirmed) so the backend deletes them. Interviewers that were never
        // registered and have no selection are simply omitted — nothing to do for them.
        attendance: attendanceData
          .filter(item => item.day_type || item.confirmed)
          .map(item => ({
            interviewer_id: item.interviewer_id,
            day_type: item.day_type || null,
            survey_ids: item.survey_ids || [],
          })),
      };
      await axios.post('/cati/attendance', payload);
      setSaveMessage('Présence enregistrée avec succès');
      fetchAttendanceData();
    } catch (error) {
      console.error('Error saving attendance:', error);
      setSaveError(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const response = await axios.get(`/cati/attendance/export?date=${dateStr}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'presence_enqueteurs_cati.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting attendance:', error);
      let msg = 'Erreur lors de l\'exportation. Veuillez réessayer.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message) msg = json.message;
        } catch (e) { /* ignore */ }
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      }
      setSaveError(msg);
    }
  };

  const surveyOptions = perDaySurveys.map(s => ({ id: s.id, title: s.title }));

  // Sort: confirmed first, then those with completes, then alphabetical.
  const sortedAttendance = [...attendanceData].sort((a, b) => {
    const aConfirmed = a.confirmed ? 0 : 1;
    const bConfirmed = b.confirmed ? 0 : 1;
    if (aConfirmed !== bConfirmed) return aConfirmed - bConfirmed;
    const aHas = (a.completes_today || 0) > 0 ? 0 : 1;
    const bHas = (b.completes_today || 0) > 0 ? 0 : 1;
    if (aHas !== bHas) return aHas - bHas;
    return (a.interviewer_name || '').localeCompare(b.interviewer_name || '', 'fr');
  });

  // Filter by name OR poste (case-insensitive)
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredAttendance = normalizedQuery
    ? sortedAttendance.filter(item => {
        const name = (item.interviewer_name || '').toLowerCase();
        const poste = String(item.poste ?? '').toLowerCase();
        return name.includes(normalizedQuery) || poste.includes(normalizedQuery);
      })
    : sortedAttendance;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Présence des Enquêteurs CATI</h1>
        <p className="text-gray-600 mt-2">Gestion de la présence quotidienne pour les enquêtes payées à la journée</p>
      </div>

      {/* Date Filters */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-red-600" />
              <span className="text-sm font-semibold text-gray-700">Date:</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Année:</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm min-w-[100px]"
              >
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Mois:</label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm min-w-[140px]"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>
                    {new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Jour:</label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm min-w-[80px]"
              >
                {getAvailableDays().map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:ml-auto sm:justify-end">
            <button
              onClick={handleExport}
              disabled={attendanceData.length === 0}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              <span>Exporter</span>
            </button>

            {!readOnly && (
              <button
                onClick={handleSave}
                disabled={!isToday() || saving || attendanceData.length === 0}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
                title={!isToday() ? 'Vous ne pouvez enregistrer que pour la date d\'aujourd\'hui' : ''}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            )}
          </div>
        </div>

        {saveMessage && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">{saveMessage}</p>
          </div>
        )}
        {saveError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{saveError}</p>
          </div>
        )}
      </div>

      {/* Search */}
      {!loading && attendanceData.length > 0 && (
        <div className="mb-4">
          <div className="relative max-w-md">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom ou poste..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
            />
          </div>
          {searchQuery && (
            <p className="mt-1 text-xs text-gray-500">
              {filteredAttendance.length} résultat{filteredAttendance.length > 1 ? 's' : ''} sur {attendanceData.length}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : attendanceData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FontAwesomeIcon icon={faUserCheck} className="text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">Aucun enquêteur CATI trouvé</p>
        </div>
      ) : filteredAttendance.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">Aucun résultat pour « {searchQuery} »</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enquêteur</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type de Journée</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Survey(s)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmation</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAttendance.map((item) => {
                  const locked = !isToday() || readOnly;                 // past days locked; supervisor_cati always read-only
                  return (
                    <tr key={item.interviewer_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          onClick={() => navigate(`/cati/interviewers/${item.interviewer_id}`)}
                          className="cursor-pointer hover:text-red-600 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900">{item.interviewer_name}</div>
                          <div className="text-xs text-gray-500 flex gap-2">
                            {item.interviewer_ref && <span>Ref: {item.interviewer_ref}</span>}
                            {item.poste && <span>Poste: {item.poste}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {item.completes_today || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`day_type_${item.interviewer_id}`}
                              value="half_day"
                              checked={item.day_type === 'half_day'}
                              onChange={() => updateRow(item.interviewer_id, { day_type: 'half_day' })}
                              onClick={() => { if (item.day_type === 'half_day') updateRow(item.interviewer_id, { day_type: null }); }}
                              disabled={locked}
                              title="Cliquer à nouveau pour désélectionner"
                              className="w-4 h-4 text-red-600 focus:ring-red-500 disabled:opacity-50"
                            />
                            <span className={`text-sm ${locked ? 'text-gray-400' : 'text-gray-700'}`}>Demi-journée</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`day_type_${item.interviewer_id}`}
                              value="full_day"
                              checked={item.day_type === 'full_day'}
                              onChange={() => updateRow(item.interviewer_id, { day_type: 'full_day' })}
                              onClick={() => { if (item.day_type === 'full_day') updateRow(item.interviewer_id, { day_type: null }); }}
                              disabled={locked}
                              title="Cliquer à nouveau pour désélectionner"
                              className="w-4 h-4 text-red-600 focus:ring-red-500 disabled:opacity-50"
                            />
                            <span className={`text-sm ${locked ? 'text-gray-400' : 'text-gray-700'}`}>Journée complète</span>
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-full min-w-[260px]">
                          <SearchableMultiSelect
                            options={surveyOptions}
                            selectedValues={item.survey_ids || []}
                            onChange={(ids) => updateRow(item.interviewer_id, { survey_ids: ids })}
                            placeholder="Sélectionner des enquêtes..."
                            searchPlaceholder="Rechercher..."
                            disabled={locked}
                            maxHeight="200px"
                            showOptionMeta={false}
                            selectedCountLabel={(count) => `${count} enquête${count > 1 ? 's' : ''}`}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.confirmed ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Confirmé
                          </span>
                        ) : isToday() ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            En attente
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewerAttendance;
